import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/auth/AuthContext';

const Header = ({ onLogin, onSignUp }) => {
  const { isUserLoggedIn, userName } = useAuth();
  const [isPurple, setIsPurple] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPhone, setIsPhone] = useState(false);
  const whiteSections = useRef([]);

  useEffect(() => {
    // Check if screen is phone size
    const checkPhone = () => {
      setIsPhone(window.innerWidth <= 480);
    };

    checkPhone();
    window.addEventListener('resize', checkPhone);

    // Check if we're on the rewards page
    const isRewardsPage = window.location.pathname === '/rewards';
    
    if (isRewardsPage) {
      // On rewards page, always show purple background
      setIsPurple(true);
    } else {
      // Find all white background sections
      whiteSections.current = Array.from(document.querySelectorAll('section')).filter(
        s => {
          const bg = window.getComputedStyle(s).background;
          return bg.includes('rgb(255, 255, 255)') || bg.includes('#fff') || bg.includes('white');
        }
      );

      const handleScroll = () => {
        const scrollY = window.scrollY;
        const headerHeight = 80; // Approximate header height
        let purple = false;
        for (const section of whiteSections.current) {
          const rect = section.getBoundingClientRect();
          const top = rect.top + window.scrollY - headerHeight;
          const bottom = top + rect.height;
          if (scrollY + headerHeight >= top && scrollY + headerHeight < bottom) {
            purple = true;
            break;
          }
        }
        setIsPurple(purple);
      };
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', checkPhone);
      };
    }
    
    return () => {
      window.removeEventListener('resize', checkPhone);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className="app-header"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transition: 'all 0.3s ease',
        background: isPurple
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : 'transparent',
        boxShadow: isPurple ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'
      }}
    >
      <div className="container">
        <div className="header-content d-flex justify-content-between align-items-center">
          <div className="header-left d-flex align-items-center gap-3">
            <h1 
              className="main-heading" 
              style={{ 
                color: 'white',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease'
              }}
              onClick={() => {
                if (isUserLoggedIn) {
                  window.location.href = '/dashboard';
                } else {
                  window.location.href = '/';
                }
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = '1';
              }}
            >
              ChatBot Factory
            </h1>
          </div>
          
          {/* Desktop/Tablet Navigation */}
          {!isPhone && (
            <div className="auth-buttons d-flex gap-3 align-items-center">
              {!isUserLoggedIn ? (
                <>
                  {/* Awards and Winners button - commented out
                  <button
                    className="winners-rewards-button"
                    onClick={() => {
                      // Open rewards page in new tab
                      window.open('/rewards', '_blank');
                    }}
                    style={{
                      background: 'transparent',
                      color: 'white',
                      border: '2px solid white',
                      padding: '8px 20px',
                      borderRadius: '99px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(255, 255, 255, 0.1)',
                      marginRight: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    🏆 Winners & Rewards
                  </button>
                  */}
                  <button
                    className="login-button"
                    onClick={onLogin} // This ensures the login popup opens
                    style={{
                      background: 'white',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      padding: '8px 20px',
                      borderRadius: '99px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.08)'
                    }}
                  >
                    Login
                  </button>
                  <button
                    className="signup-button"
                    onClick={onSignUp} // This ensures the signup popup opens
                    style={{
                      background: 'transparent',
                      color: 'white',
                      border: '2px solid white',
                      padding: '8px 20px',
                      borderRadius: '99px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <div className="user-info">
                  <span className="user-name" style={{ color: 'white' }}>Welcome, {userName}!</span>
                  <button
                    className="dashboard-link"
                    onClick={() => window.location.href = '/dashboard'}
                    style={{ background: 'none', border: 'none', color: 'white', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Go to Dashboard
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Phone Hamburger Menu */}
          {isPhone && (
            <div className="phone-menu">
              <button
                className="hamburger-button"
                onClick={toggleMobileMenu}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
              >
                <span style={{
                  width: '25px',
                  height: '3px',
                  background: 'white',
                  transition: 'all 0.3s ease',
                  transform: isMobileMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
                }}></span>
                <span style={{
                  width: '25px',
                  height: '3px',
                  background: 'white',
                  transition: 'all 0.3s ease',
                  opacity: isMobileMenuOpen ? '0' : '1'
                }}></span>
                <span style={{
                  width: '25px',
                  height: '3px',
                  background: 'white',
                  transition: 'all 0.3s ease',
                  transform: isMobileMenuOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'none'
                }}></span>
              </button>

              {/* Phone Menu Overlay */}
              {isMobileMenuOpen && (
                <div className="phone-menu-overlay" style={{
                  position: 'fixed',
                  top: '0',
                  left: '0',
                  right: '0',
                  bottom: '0',
                  background: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 999,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  paddingTop: '100px'
                }} onClick={closeMobileMenu}>
                  <div className="phone-menu-content" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '16px',
                    padding: '32px',
                    width: '90%',
                    maxWidth: '350px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                  }} onClick={(e) => e.stopPropagation()}>
                    {!isUserLoggedIn ? (
                      <>
                        {/* Awards and Winners button - commented out
                        <button
                          className="phone-menu-button"
                          onClick={() => {
                            window.open('/rewards', '_blank');
                            closeMobileMenu();
                          }}
                          style={{
                            background: 'transparent',
                            color: 'white',
                            border: '2px solid white',
                            padding: '12px 20px',
                            borderRadius: '99px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          🏆 Winners & Rewards
                        </button>
                        */}
                        <button
                          className="phone-menu-button"
                          onClick={() => {
                            onLogin();
                            closeMobileMenu();
                          }}
                          style={{
                            background: 'white',
                            color: '#667eea',
                            border: '2px solid white',
                            padding: '12px 20px',
                            borderRadius: '99px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Login
                        </button>
                        <button
                          className="phone-menu-button"
                          onClick={() => {
                            onSignUp();
                            closeMobileMenu();
                          }}
                          style={{
                            background: 'transparent',
                            color: 'white',
                            border: '2px solid white',
                            padding: '12px 20px',
                            borderRadius: '99px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Sign Up
                        </button>
                      </>
                    ) : (
                      <div className="phone-user-info" style={{
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        <div style={{ marginBottom: '16px' }}>
                          <span style={{ fontWeight: '600' }}>Welcome, {userName}!</span>
                        </div>
                        <button
                          className="phone-dashboard-link"
                          onClick={() => {
                            window.location.href = '/dashboard';
                            closeMobileMenu();
                          }}
                          style={{
                            background: 'white',
                            color: '#667eea',
                            border: 'none',
                            padding: '12px 20px',
                            borderRadius: '99px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Go to Dashboard
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header; 